import { AxiosError } from 'axios';
import type { ApiError, CandidateStage, CandidateSource } from '@pms/shared';

export function getErrorMessage(e: unknown): string {
  const ax = e as AxiosError<ApiError>;
  return ax.response?.data?.error?.message ?? 'Something went wrong';
}

export function fullName(first?: string | null, last?: string | null): string {
  return `${first ?? ''} ${last ?? ''}`.trim() || '—';
}

export function initials(first?: string | null, last?: string | null): string {
  const a = (first ?? '').trim();
  const b = (last ?? '').trim();
  const res = `${a[0] ?? ''}${b[0] ?? ''}`.toUpperCase();
  return res || '?';
}

export function formatDate(value?: string | null): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

export const STAGE_META: Record<CandidateStage, { label: string; className: string }> = {
  new:            { label: 'New',            className: 'bg-teal-50 text-teal-700 border-teal-200 dark:bg-teal-900/20 dark:text-teal-400 dark:border-teal-900/40' },
  screening:      { label: 'Screening',      className: 'bg-violet-50 text-violet-700 border-violet-200 dark:bg-violet-900/20 dark:text-violet-400 dark:border-violet-900/40' },
  interview:      { label: 'Interview',      className: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/20 dark:text-amber-400 dark:border-amber-900/40' },
  selected:       { label: 'Selected',       className: 'bg-teal-50 text-teal-700 border-teal-200 dark:bg-teal-900/20 dark:text-teal-400 dark:border-teal-900/40' },
  offer_released: { label: 'Offer Released', className: 'bg-violet-50 text-violet-700 border-violet-200 dark:bg-violet-900/20 dark:text-violet-400 dark:border-violet-900/40' },
  offer_accepted: { label: 'Offer Accepted', className: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-400 dark:border-emerald-900/40' },
  offer_rejected: { label: 'Offer Rejected', className: 'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-900/20 dark:text-rose-400 dark:border-rose-900/40' },
  rejected:       { label: 'Rejected',       className: 'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-900/20 dark:text-rose-400 dark:border-rose-900/40' },
  converted:      { label: 'Converted',      className: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-400 dark:border-emerald-900/40' },
  on_hold:        { label: 'On Hold',        className: 'bg-slate-100 text-slate-600 border-slate-200 dark:bg-muted dark:text-slate-400 dark:border-border' },
};

export const SOURCE_OPTIONS: { value: CandidateSource; label: string }[] = [
  { value: 'referral', label: 'Referral' },
  { value: 'job_portal', label: 'Job Portal' },
  { value: 'walk_in', label: 'Walk In' },
  { value: 'agency', label: 'Agency' },
  { value: 'social_media', label: 'Social Media' },
  { value: 'campus', label: 'Campus' },
  { value: 'other', label: 'Other' },
];

/** Seeded default sections offered as suggestions; admins can add custom ones. */
export const DEFAULT_SECTIONS = [
  'documentation', 'hr_formalities', 'it_assets',
  'workspace_orientation', 'trial_confirmation', 'exit', 'general',
];

const KNOWN_SECTION_LABELS: Record<string, string> = {
  documentation:         'Documentation',
  hr_formalities:        'HR Formalities',
  it_assets:             'IT & Assets',
  workspace_orientation: 'Workspace & Orientation',
  trial_confirmation:    'Trial & Confirmation',
  exit:                  'Exit Process',
  general:               'General',
};

/** Human label for a section: known slugs get a curated name; custom ones are title-cased. */
export function sectionLabel(section: string): string {
  return (
    KNOWN_SECTION_LABELS[section] ??
    section.replace(/[_-]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
  );
}
